// ReportHeaderProductivityGraph component

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import $ from 'jquery'
import _ from 'lodash'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'

import Util from '../../../lib/util.jsx'
import DateUtil from '../../../lib/dateutil.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'

class ReportHeaderProductivityGraphV2 extends Component {
  static get propTypes () {
    return {
      data: PropTypes.array,
      filterType: PropTypes.string,
      graphFromDate: PropTypes.object,
      graphToDate: PropTypes.object,
      fromDate: PropTypes.object,
      toDate: PropTypes.object
    }
  }

  static get defaultProps () {
    return {
      data: [],
      filterType: '',
      graphFromDate: null,
      graphToDate: null,
      fromDate: null,
      toDate: null
    }
  }

  constructor (props) {
    super(props)

    this.data = {
      width: 0,
      height: 0
    }

    this._resizeHandler = this._resize.bind(this)
    this._references = {
      graphWrap: React.createRef()
    }
  }

  componentDidMount () {
    if (this._references.graphWrap.current) {
      const {
        width,
        height
      } = this._references.graphWrap.current.getBoundingClientRect()
      this.data.width = width
      this.data.height = height

      this.setState({ dataDate: new Date() })
    }

    $(window).on('resize', this._resizeHandler)
  }

  componentWillUnmount () {
    $(window).off('resize', this._resizeHandler)
  }

  render () {
    const {
      data,
      filterType,
      graphFromDate,
      graphToDate,
      fromDate,
      toDate
    } = this.props
    const labelAvailable = IntlUtil.convertMessage(
      'report.header.labelAvailable'
    )
    const labelPlanned = IntlUtil.convertMessage('report.header.labelPlanned')
    const labelAway = IntlUtil.convertMessage('report.header.labelAway')
    const labelTracked = IntlUtil.convertMessage('report.header.labelTracked')

    // Find maximum value in Y-Axis
    const maxYaxisVal = 'labelMaxYaxisVal'
    const maxYValues = data.map((d) => {
      return _.max(
        [
          d.totalAvailable,
          d.totalPlanned,
          d.totalAway,
          d.totalTracked,
          d.totalAvailableHighlight,
          d.totalPlannedHighlight,
          d.totalAwayHighlight
        ],
        (maxVal) => maxVal
      )
    })
    const maxYValue = _.max(maxYValues)

    const colorDataAvailable = []
    const colorDataPlanned = []
    const colorDataAway = []
    const lastDataIdx = data.length - 1
    const fromDateForColor = new Date(fromDate)
    const toDateForColor = new Date(toDate)

    if (
      fromDateForColor.getFullYear() === toDateForColor.getFullYear() &&
      fromDateForColor.getMonth() === toDateForColor.getMonth() &&
      fromDateForColor.getDate() === toDateForColor.getDate()
    ) {
      toDateForColor.setDate(toDateForColor.getDate() + 1)
    }

    const graphData = data.map((d, idx) => {
      const dRow = {
        ident: d.ident,
        isWeekEnd: DateUtil.getDisplayIsWeekEnd(new Date(d.date))
      }

      dRow[labelAvailable] = d.totalAvailable
      dRow[labelPlanned] = d.totalPlanned
      dRow[labelAway] = d.totalAway
      dRow[labelTracked] = d.totalTracked
      dRow[maxYaxisVal] = maxYValue + Math.ceil(maxYValue / 5)

      const startOffset = (idx / lastDataIdx) * 100
      const endOffset = ((idx + 1) / lastDataIdx) * 100
      const bHighlight = this._isHighlightData(
        d,
        filterType,
        fromDateForColor,
        toDateForColor
      )
      const colorAvailable = bHighlight ? '#4dbce9' : '#336a91'
      const colorPlanned = bHighlight ? '#7bbc9a' : '#406c79'
      const colorAway = bHighlight ? '#e9ecf1' : '#7b90a3'

      colorDataAvailable.push(
        { color: colorAvailable, offset: startOffset },
        { color: colorAvailable, offset: endOffset }
      )
      colorDataPlanned.push(
        { color: colorPlanned, offset: startOffset },
        { color: colorPlanned, offset: endOffset }
      )
      colorDataAway.push(
        { color: colorAway, offset: startOffset },
        { color: colorAway, offset: endOffset }
      )

      return dRow
    })

    const filterItems = []
    if (filterType === 'year') {
      filterItems.push({
        label: graphFromDate.getFullYear()
      })
    } else if (filterType === 'week') {
      filterItems.push({
        label: DateUtil.getDisplayMonthByNumber(graphFromDate.getMonth())
      })
    } else if (filterType === 'month' && graphFromDate && graphToDate) {
      let tempDate = new Date(graphFromDate)
      while (tempDate.getTime() <= graphToDate.getTime()) {
        const newWeekNo = DateUtil.getDisplayWeek(tempDate)
        filterItems.push({
          label: `${IntlUtil.convertMessage(
            'report.header.weekLabel'
          )} ${newWeekNo}`
        })

        tempDate = new Date(tempDate.setDate(tempDate.getDate() + 7))
      }
    }

    const labelClass =
      filterItems && filterItems.length === 5 ? 'itemlabel alt' : 'itemlabel'

    return (
      <div className='report-productivitygraph-wrap'>
        <div className='labelwrap'>
          {filterItems.map((filterItem, idx) => (
            <div className={labelClass} key={idx}>
              {filterItem.label}
            </div>
          ))}
        </div>
        <div
          className='report-productivitygraph'
          ref={this._references.graphWrap}
        >
          <AreaChart
            data={graphData}
            width={this.data.width}
            height={this.data.height}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id='colorAvailable'>
                {colorDataAvailable.map((colorData, idx) => (
                  <stop
                    key={idx}
                    offset={`${colorData.offset}%`}
                    stopColor={colorData.color}
                    stopOpacity='1'
                  />
                ))}
              </linearGradient>
              <linearGradient id='colorPlanned'>
                {colorDataPlanned.map((colorData, idx) => (
                  <stop
                    key={idx}
                    offset={`${colorData.offset}%`}
                    stopColor={colorData.color}
                    stopOpacity='1'
                  />
                ))}
              </linearGradient>
              <linearGradient id='colorAway'>
                {colorDataAway.map((colorData, idx) => (
                  <stop
                    key={idx}
                    offset={`${colorData.offset}%`}
                    stopColor={colorData.color}
                    stopOpacity='1'
                  />
                ))}
              </linearGradient>
            </defs>
            <XAxis
              dataKey='ident'
              tick={this._customizedLabel.bind(this, graphData)}
              tickFormatter={(d) => {
                return this._formatAxisLabel(d)
              }}
            />
            <YAxis
              tick={{ fill: '#fff' }}
              tickCount={5}
              tickFormatter={(d) => {
                return d
              }}
            />
            <CartesianGrid stroke='#496481' />
            <Tooltip
              wrapperStyle={{
                background: '#1f415d',
                border: 'none',
                boxShadow: 'none'
              }}
              labelStyle={{ display: 'none' }}
              itemStyle={{ fontSize: '12px' }}
              content={this._renderTooltipContent.bind(this)}
            />
            <Area
              type='monotone'
              dataKey={labelAvailable}
              stroke='url(#colorAvailable)'
              strokeWidth={2}
              fill='url(#colorAvailable)'
              fillOpacity={0.8}
              isAnimationActive={false}
            />
            <Area
              type='monotone'
              dataKey={labelPlanned}
              stroke='url(#colorPlanned)'
              strokeWidth={2}
              fill='url(#colorPlanned)'
              fillOpacity={0.9}
              isAnimationActive={false}
            />
            <Area
              type='monotone'
              dataKey={labelAway}
              stroke='url(#colorAway)'
              strokeWidth={2}
              fill='url(#colorAway)'
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Area
              type='monotone'
              dataKey={maxYaxisVal}
              stroke='#ffffff'
              strokeWidth={0}
              fill='#e9ecf1'
              fillOpacity={0}
              isAnimationActive={false}
            />
          </AreaChart>
        </div>
      </div>
    )
  }

  /* Private functions */
  _renderTooltipContent (props) {
    const {
      payload,
      separator,
      formatter,
      itemStyle,
      itemSorter,
      wrapperStyle
    } = props
    const finalStyle = {
      margin: 0,
      padding: 10,
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      whiteSpace: 'nowrap',
      ...wrapperStyle
    }

    if (payload && payload.length) {
      const listStyle = { padding: 0, margin: 0 }
      const labelAvailable = IntlUtil.convertMessage(
        'report.header.labelAvailable'
      )
      const labelPlanned = IntlUtil.convertMessage(
        'report.header.labelPlanned'
      )
      const labelAway = IntlUtil.convertMessage('report.header.labelAway')

      const items = payload.sort(itemSorter).map((entry, i) => {
        if (
          entry.dataKey === 'labelAvailableHighlight' ||
          entry.dataKey === 'labelPlannedHighlight' ||
          entry.dataKey === 'labelTrackedHighlight' ||
          entry.dataKey === 'labelMaxYaxisVal'
        ) {
          return null
        }

        const awayTimeTaken = entry.payload
          ? entry.payload[`${labelAway}`] * 60
          : 0
        const plannedTimeTaken = entry.payload
          ? (entry.payload[`${labelPlanned}`] - entry.payload[`${labelAway}`]) *
            60
          : 0
        const availableTimeTaken = entry.payload
          ? (entry.payload[`${labelAvailable}`] -
              entry.payload[`${labelPlanned}`]) *
            60
          : 0

        if (entry.dataKey === labelAvailable) {
          entry.color = '#4dbce9'
          entry.value =
            availableTimeTaken > 0
              ? Util.convertMinutesToHour(availableTimeTaken)
              : 0
        } else if (entry.dataKey === labelPlanned) {
          entry.color = '#7bbc9a'
          entry.value = plannedTimeTaken
            ? Util.convertMinutesToHour(plannedTimeTaken)
            : 0
        } else if (entry.dataKey === labelAway) {
          entry.color = '#fff'
          entry.value = awayTimeTaken
            ? Util.convertMinutesToHour(awayTimeTaken)
            : 0
        }

        const finalItemStyle = {
          display: 'block',
          paddingTop: 4,
          paddingBottom: 4,
          color: entry.color || '#000',
          ...itemStyle
        }

        const defaultFormatter = (value) => value
        const finalFormatter = entry.formatter || formatter || defaultFormatter

        return (
          <li
            className='recharts-tooltip-item'
            key={`tooltip-item-${i}`}
            style={finalItemStyle}
          >
            <span className='recharts-tooltip-item-name'>{entry.name}</span>
            <span className='recharts-tooltip-item-separator'>{separator}</span>
            <span className='recharts-tooltip-item-value'>
              {finalFormatter
                ? finalFormatter(entry.value, entry.name, entry, i)
                : entry.value}
            </span>
            <span className='recharts-tooltip-item-unit'>
              {entry.unit || ''}
            </span>
          </li>
        )
      })

      return (
        <div className='recharts-default-tooltip' style={finalStyle}>
          <ul className='recharts-tooltip-item-list' style={listStyle}>
            {items}
          </ul>
        </div>
      )
    }

    return null
  }

  _resize () {
    if (this._references.graphWrap.current) {
      const {
        width,
        height
      } = this._references.graphWrap.current.getBoundingClientRect()

      this.data.width = width
      this.data.height = height

      this.setState({ dataDate: new Date() })
    }
  }

  _formatAxisLabel (d) {
    if (this.props.filterType === 'year') {
      return DateUtil.getDisplayMonthByNumber(d - 1)
    }

    return d
  }

  _customizedLabel (graphData, props) {
    const { x, y, payload, width, height, stroke, textAnchor, index } = props
    let fill = '#fff'
    if (this.props.filterType !== 'year') {
      fill = graphData[index].isWeekEnd ? '#455f7a' : '#fff'
    }

    return (
      <text
        x={x}
        y={y}
        fill={fill}
        className='recharts-text recharts-cartesian-axis-tick-value'
        width={width}
        height={height}
        stroke={stroke}
        textAnchor={textAnchor}
      >
        <tspan x={x} dy='0.71em'>
          {this._formatAxisLabel(payload.value)}
        </tspan>
      </text>
    )
  }

  _isHighlightData (data, filterType, fromDate, toDate) {
    let bHighlight = false
    const date = new Date(data.date)

    if (filterType === 'year') {
      const fromDateYear = fromDate.getFullYear()
      const fromDateMonth = fromDate.getMonth()
      const toDateYear = toDate.getFullYear()
      const toDateMonth = toDate.getMonth()
      const graphYear = date.getFullYear()
      const graphMonth = date.getMonth()

      if (fromDateYear === graphYear && toDateYear === graphYear) {
        if (graphMonth >= fromDateMonth && graphMonth <= toDateMonth) {
          bHighlight = true
        }
      } else if (fromDateYear === graphYear && graphMonth >= fromDateMonth) {
        bHighlight = true
      } else if (toDateYear === graphYear && graphMonth <= toDateMonth) {
        bHighlight = true
      }
    } else {
      const itemDateTime = date.getTime()
      if (
        itemDateTime >= fromDate.getTime() &&
        itemDateTime < toDate.getTime()
      ) {
        bHighlight = true
      }
    }

    return bHighlight
  }
}

export default ReportHeaderProductivityGraphV2
